import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modals, Routes } from '@/constants';
import { EVENT_CONSTANTS } from '@/services';
import {
  playerSessionsSelectors,
  playerThunks,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsEpisodePlaying,
  selectIsEpisodeRedeemRequired,
  selectIsFreeTrialUser,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useFetchChapterQuery,
} from '@/store';
import { EpisodeCard, Referrer } from '@/types';
import { createRouteWithParams } from '@/utils';

import { selectEpisodePlayerPosition } from '../../selectors';
import { EpisodeToolbar as EpisodeToolbarComponent } from './EpisodeToolbar.component';

export const EpisodeToolbar = ({
  episode,
  referrer,
}: {
  episode: EpisodeCard;
  referrer: Referrer;
}) => {
  const { episodeId } = episode;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const playerPosition =
    useAppSelector(state => selectEpisodePlayerPosition(state, episodeId)) ?? 0;
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);
  const activeChapterId = useAppSelector(selectActiveChapterId);
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId),
  );
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );
  const isFreeTrialUser = useAppSelector(selectIsFreeTrialUser);
  const isEpisodeRedeemRequired = useAppSelector(state =>
    selectIsEpisodeRedeemRequired(state, episodeId),
  );

  const episodeStatus = playerSession?.status;
  const episodeDuration = episode?.duration;
  const playedDuration = playerSession?.playedDuration || 0;

  const isEpisodePlaying = useAppSelector(state =>
    selectIsEpisodePlaying(state, episodeId),
  );
  const isTrailerActive =
    activeEpisodeId === episodeId && activeChapterId === episode.trailerId;

  const { trailerDuration = 0 } = useFetchChapterQuery(
    {
      chapterId: episode.trailerId,
      compliance,
    },
    {
      skip: !isTrailerActive,
      selectFromResult: result => ({
        trailerDuration: result.data?.duration,
      }),
    },
  );

  const navigateToEpisode = useCallback(() => {
    navigate(
      {
        pathname: createRouteWithParams(Routes.EPISODE, {
          episodeId,
        }),
      },
      {
        state: { referrer },
      },
    );
  }, [navigate, episodeId, referrer]);

  const onClickHandler = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (!episodeStatus || episodeStatus === 'none') {
        dispatch(
          playerThunks.chapterPlayPressed({
            activeChapter: { episodeId, chapterId: episode.trailerId },
            location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.PREVIEW },
            referrer,
          }),
        );
      }
      if (episodeStatus === 'onGoing') {
        dispatch(
          playerThunks.episodePlayPressed({
            episodeId,
            location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.PLAY },
            referrer,
          }),
        );
      }
      if (episodeStatus === 'quizReady') {
        navigateToEpisode();
        dispatch(
          uiActions.setActiveModal({
            name: Modals.CHAPTER_QUIZ,
            params: {
              episodeId,
              showFirstIncompleteQuiz: true,
            },
          }),
        );
      }
      if (episodeStatus === 'assessmentReady') {
        if (isEpisodeRedeemRequired) {
          dispatch(
            playerThunks.initiateRedemption({
              episodeId,
              location: {
                ...referrer,
                button: EVENT_CONSTANTS.BUTTON.ASSESSMENT,
              },
            }),
          );
        } else {
          const assessmentUrl = createRouteWithParams(
            Routes.EPISODE_ASSESSMENT,
            { episodeId },
          );
          navigate(assessmentUrl);
        }
      }
      if (episodeStatus === 'verified') {
        navigateToEpisode();
      }
    },
    [
      navigate,
      navigateToEpisode,
      dispatch,
      episode.trailerId,
      episodeId,
      referrer,
      episodeStatus,
      isEpisodeRedeemRequired,
    ],
  );

  return (
    <EpisodeToolbarComponent
      episodeStatus={episodeStatus}
      isTrailerActive={isTrailerActive}
      isEpisodePlaying={isEpisodePlaying}
      onClick={onClickHandler}
      playerPosition={playerPosition}
      episodePlayedDuration={playedDuration}
      trailerDuration={trailerDuration}
      episodeDuration={episodeDuration}
      showPremium={!!(episode?.isPremium && isFreeTrialUser)}
    />
  );
};
